import {AfterContentChecked, AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AuthService} from '../services/auth/auth.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {RouteService} from '../services/route/route.service';
import {CartService} from '../services/cart/cart.service';
import {LanguageService} from '../services/language/language.service';
import {StorageService} from '../services/storage/storage.service';
import {TranslateService} from '@ngx-translate/core';
import {UtilService} from '../services/util/util.service';
import {ViewService} from '../services/view/view.service';
import {FiscalService} from '../services/fiscal/fiscal.service';
import {RefundService} from '../services/refund/refund.service';
import {environment} from '../../environments/environment';
import {find} from 'lodash-es';
import {throttleTime, Subject, Subscription, asyncScheduler} from 'rxjs';
import {MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions} from '@angular/material/tooltip';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {NavMenuComponent} from './menus/nav-menu/nav-menu.component';
import {ConfirmDialogComponent} from '../components/confirm-dialog/confirm-dialog.component';
import {MatSidenav} from '@angular/material/sidenav';
import moment from 'moment';
import {EventPurchaseComponent} from '../modules/sales-terminal/event-purchase/event-purchase.component';
import {STORAGE_REPORTS_LEGAL_PERSON_ID, STORAGE_REPORTS_SUMMARY_REPORT_FILTERS} from "../modules/reports/reports.constants";

export interface MenuItem {
  route: string;
  label: string;
  icon?: string;
  hasSpacer?: boolean;
  canAccess?: () => boolean;
  primaryItems?: MenuItem[];
  primaryCategories?: MenuCategory[];
  items?: MenuItem[];
  categories?: MenuCategory[];
}

export interface MenuCategory {
  label: string;
  icon: string;
  canAccess: () => boolean;
  items: MenuItem[];
}

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LayoutComponent implements OnInit, OnDestroy, AfterViewInit, AfterContentChecked {
  static STORAGE_LAYOUT_MENU_EXTENDED = 'layout.isMenuExtended';

  @ViewChild('pageContent', { static: true }) pageContent: ElementRef;
  @ViewChild('sidenav', { static: true }) sidenav: MatSidenav;

  private currentUrl: string = '';
  private subscriptions: Subscription[] = [];
  private scrollSubject = new Subject<any>();
  public sidenavExpanded: boolean = true;
  public sidenavExtended: boolean = false;
  public isScrolled: boolean = false;
  public prevScrolled: boolean = false;
  public menuItems: MenuItem[] = [
    {
      route: 'sales-terminal',
      label: 'general.api_menu_salespoint_sales',
      canAccess: () => this.authService.canAccessSales,
      categories: [],
      items: [
        {
          route: '',
          label: 'general.api_menu_search',
          icon: 'search',
          canAccess: () => true
        },
        {
          route: '/transactions',
          label: 'general.api_menu_transactions',
          icon: 'transaction',
          canAccess: () => true
        },
        {
          route: '/tickets',
          label: 'general.api_menu_tickets',
          icon: 'ticket',
          canAccess: () => this.authService.hasTicketToolPrivilege
        },
        {
          route: '/invoices',
          label: 'general.api_menu_invoices',
          icon: 'invoice',
          canAccess: () => !environment.production
        },
        {
          route: '/bookings',
          label: 'general.api_menu_reservations',
          icon: 'bookings',
          canAccess: () => this.authService.hasBookingViewPrivilege
        },
        {
          route: '/refund',
          label: 'general.api_menu_refund',
          icon: 'refresh',
          canAccess: () => this.authService.hasRefundAccessPrivilege && this.hasRefundTickets
        },
        {
          route: '/payment-reversal',
          label: 'general.api_menu_payment_reversals',
          icon: 'refresh',
          canAccess: () => this.authService.hasSelfServicePaymentReversalPrivilege
        }
      ]
    },
    {
      route: 'back-office',
      label: 'general.api_menu_back_office',
      canAccess: () => this.authService.canAccessBackOffice,
      categories: [],
      items: [
        {
          route: '/dashboard',
          label: 'general.api_menu_events_dashboard',
          icon: 'templates',
          canAccess: () => this.authService.hasAdminDashboardPrivilege
        },
        {
          route: '/events',
          label: 'general.api_menu_events',
          icon: 'bookings',
          canAccess: () => this.authService.hasSelfServiceEventsAccessPrivilege
        },
        {
          route: '/recently-finished',
          label: 'general.api_menu_recently_finished',
          icon: 'booking_success',
          canAccess: () => this.authService.hasAdminDashboardPrivilege
        },
        {
          route: '/venues',
          label: 'general.api_menu_venues',
          icon: 'public_venues',
          canAccess: () => true
        }
      ]
    },
    {
      route: 'admin',
      label: 'general.api_menu_admin',
      canAccess: () => this.authService.canAccessAdmin,
      primaryCategories: [
        {
          label: 'general.api_menu_accounts',
          icon: 'accounts',
          canAccess: () => this.authService.canAccessAccounts,
          items: [
            {
              route: '/users',
              label: 'general.api_menu_users',
              icon: 'user',
              canAccess: () => this.authService.hasUserGetPrivilege
            },
            {
              route: '/legal-persons',
              label: 'general.api_menu_legal_persons',
              icon: 'legal_person',
              canAccess: () => this.authService.hasLegalGetPrivilege
            },
            {
              route: '/customers',
              label: 'general.api_menu_customers',
              icon: 'group',
              canAccess: () => this.authService.hasCustomersAccessPrivileges
            },
            {
              route: '/roles',
              label: 'general.api_menu_roles',
              icon: 'roles',
              hasSpacer: true,
              canAccess: () => this.authService.hasRolesAccessPrivilege
            },
            {
              route: '/privileges',
              label: 'general.api_menu_privileges',
              icon: 'favourite',
              canAccess: () => this.authService.hasPrivilegesManagePrivilege
            }
          ]
        },
        {
          label: 'general.api_menu_classifiers',
          icon: 'classifiers',
          canAccess: () => this.authService.canAccessClassifiers,
          items: [
            {
              route: '/salespoints',
              label: 'general.api_menu_salespoints',
              icon: 'salespoints',
              canAccess: () => this.authService.hasSalespointManagePrivilege
            },
            {
              route: '/categories',
              label: 'general.api_menu_categories',
              icon: 'thumbnail_view',
              canAccess: () => this.authService.hasCategoriesAccessPrivilege
            }
          ]
        },
        {
          label: 'general.api_menu_translations',
          icon: 'text',
          canAccess: () => this.authService.hasTranslationsPrivilege,
          items: [
            {
              route: '/translations',
              label: 'general.api_menu_translations_ui',
              icon: 'translations',
              canAccess: () => this.authService.hasTranslationsPrivilege
            },
            {
              route: '/templates',
              label: 'general.api_menu_templates',
              icon: 'templates',
              canAccess: () => this.authService.hasTranslationsPrivilege
            }
          ]
        },
        {
          label: 'general.api_menu_fees',
          icon: 'invoice',
          canAccess: () => this.authService.hasFeesAccessPrivilege,
          items: [
            {
              route: '/commission-fees',
              label: 'general.api_menu_commission_fees',
              icon: 'invoice',
              canAccess: () => this.authService.hasFeesAccessPrivilege
            },
            {
              route: '/service-fees',
              label: 'general.api_menu_service_fees',
              icon: 'invoice',
              canAccess: () => this.authService.hasFeesAccessPrivilege
            },
            {
              route: '/delivery-types',
              label: 'general.api_menu_delivery_types',
              icon: 'invoice',
              canAccess: () => this.authService.hasFeesAccessPrivilege
            }
          ]
        }
      ],
      categories: [],
      items: [
        {
          route: '/api-users',
          label: 'general.api_menu_apiusers',
          icon: 'api',
          canAccess: () => this.authService.hasApiUsersPrivilege
        },
        {
          route: '/registration-data',
          label: 'general.api_menu_registration_data',
          icon: 'trash',
          canAccess: () => this.authService.hasRegistrationDataPrivilege
        }
      ]
    },
    {
      route: 'reports',
      label: 'general.api_menu_reports',
      canAccess: () => this.authService.canAccessReports,
      primaryItems: [
        {
          route: '/daily-reports',
          label: 'general.api_menu_daily_reports',
          icon: 'pie_graph',
          canAccess: () => this.authService.hasDailyReportPrivilege
        },
        {
          route: '/salespoint-sales',
          label: 'general.api_menu_salespoint_sales_reports',
          icon: 'pie_graph',
          canAccess: () => this.authService.hasReportingSalespointSalesPrivilege
        }
      ],
      primaryCategories: [
        {
          label: 'general.api_menu_event_owner_reports',
          icon: 'bar_graph',
          canAccess: () => this.authService.hasReportingPromoterDashboardPrivilege || this.authService.hasReportingPromoterMyEventsPrivilege || this.authService.hasReportingPromoterDetailedReportsPrivilege || this.authService.hasOverEventsReportPrivilege || this.authService.hasReportingSalesSummaryReportPrivilege,
          items: [
            {
              route: '/overview',
              label: 'general.api_menu_overview',
              icon: 'search',
              canAccess: () => this.authService.hasReportingPromoterDashboardPrivilege
            },
            {
              route: '/events',
              label: 'general.api_menu_events_reports',
              icon: 'bookings',
              canAccess: () => this.authService.hasReportingPromoterMyEventsPrivilege
            },
            {
              route: '/detailed-reports',
              label: 'general.api_menu_detailed_reports',
              icon: 'bar_graph',
              canAccess: () => this.authService.hasReportingPromoterDetailedReportsPrivilege
            },
            {
              route: '/commission-fees',
              label: 'general.api_menu_commission_fee_report',
              icon: 'invoice',
              canAccess: () => !!this.viewService.conf.environment?.length && this.authService.hasReportingPromoterMyEventsPrivilege
            },
            {
              route: '/over-events-report',
              label: 'general.api_menu_over_events_report',
              icon: 'sales_period',
              canAccess: () => this.authService.hasOverEventsReportPrivilege
            },
            {
              route: '/sales-summary-report',
              label: 'general.api_menu_events_summary_report',
              icon: 'bar_graph',
              canAccess: () => this.authService.hasReportingSalesSummaryReportPrivilege
            }
          ]
        }
      ],
      categories: [
        {
          label: 'general.api_menu_ticket_personalisation_reports',
          icon: 'bulk_change',
          canAccess: () => this.authService.hasRegistrationReportPrivilege || this.authService.hasTicketRegistrationPrivilege,
          items: [
            {
              route: '/ticket-registration-reports',
              label: 'general.api_menu_ticket_registration_reports',
              icon: 'ticket',
              canAccess: () => this.authService.hasRegistrationReportPrivilege
            },
            {
              route: '/ticket-registration',
              label: 'general.api_menu_ticket_registration_reports_by_one',
              icon: 'ticket_info',
              canAccess: () => this.authService.hasTicketRegistrationPrivilege
            }
          ]
        },
        {
          label: 'general.api_menu_accounting_reports',
          icon: 'archive',
          canAccess: () => this.authService.hasLegalInvoicingViewPrivilege || this.authService.hasBalanceSummaryViewPrivilege,
          items: [
            {
              route: '/invoices-report',
              label: 'general.api_menu_invoices_report',
              icon: 'invoice',
              canAccess: () => this.authService.hasLegalInvoicingViewPrivilege
            },
            {
              route: '/balance-summary',
              label: 'general.api_menu_balance_summary_report',
              icon: 'bar_graph',
              canAccess: () => this.authService.hasBalanceSummaryViewPrivilege
            }
          ]
        }
      ],
      items: [
        {
          route: '/venue-events',
          label: 'general.api_menu_venue_owner_events_reports',
          icon: 'bookings',
          canAccess: () => this.authService.hasReportingVenueOwnerPrivilege
        }
      ]
    },
    {
      route: 'help',
      label: 'general.api_menu_help',
      canAccess: () => true,
      categories: [],
      items: []
    }
  ];
  public caller: boolean = false;
  public activeCategory: MenuCategory;
  public navOpen: boolean;

  constructor(
    public authService: AuthService,
    public routeService: RouteService,
    public cartService: CartService,
    public languageService: LanguageService,
    public viewService: ViewService,
    public fiscalService: FiscalService,
    private router: Router,
    private dialog: MatDialog,
    private storageService: StorageService,
    private translateService: TranslateService,
    public utilService: UtilService,
    private changeDetector: ChangeDetectorRef,
    private refundService: RefundService,
    private activatedRoute: ActivatedRoute,
    @Inject(MAT_TOOLTIP_DEFAULT_OPTIONS)
      _tooltipOptions: MatTooltipDefaultOptions
  ) {
    _tooltipOptions.position = 'above';
    if (storageService.get(LayoutComponent.STORAGE_LAYOUT_MENU_EXTENDED)) {
      this.sidenavExpanded = !!storageService.get(LayoutComponent.STORAGE_LAYOUT_MENU_EXTENDED);
    }
    if (!routeService.isWindow && this.isAuthorized) {
      router.events.subscribe((route: any) => {
        if (route instanceof NavigationEnd) {
          this.currentUrl = route.url === '/' ? ('/app/sales-terminal') : (route.urlAfterRedirects || route.url);
          if (!routeService.checkRoute(['reports'], route.url)) {
            this.storageService.remove(STORAGE_REPORTS_LEGAL_PERSON_ID);
          }
          if (!routeService.checkRoute(['reports', 'sales-summary-report'], route.url)) {
            this.storageService.remove(STORAGE_REPORTS_SUMMARY_REPORT_FILTERS);
          }
          this.getActiveCategory();
        }
      });
    }
    router.onSameUrlNavigation = 'reload';
  }

  get keyboardHeight(): number {
    return this.utilService.virtualKeyboardHeight;
  }

  get fixedSnackHeight(): string {
    return 'margin-top: ' + this.utilService.fixedSnackHeight + 'px';
  }

  get isAuthorized(): boolean {
    return this.authService.isLoggedIn;
  }

  get hasSpOverlay(): boolean {
    return (this.routeService.checkRoute(['sales-terminal']) || this.routeService.url === '/') && this.authService.checkSalespointProperty('fiscal_procedures_by') && !this.fiscalService.isReady;
  }

  get hasRefundTickets(): boolean {
    return !!(this.refundService.refundEvents?.length);
  }

  public ngOnInit(): void {
    this.utilService.hasHeader = true;
    this.router.navigate([], {queryParams: this.activatedRoute.snapshot.queryParams, queryParamsHandling: 'merge'});
    if (this.authService.canAccessSales && !this.routeService.checkRoute(['sales-terminal', 'cart'])) {
      this.cartService.cartSubscription = this.cartService.getCart().subscribe();
      this.subscriptions.push(this.cartService.cartSubscription);
    }
    if (this.authService.hasRefundAccessPrivilege) {
      this.refundService.updateCart();
    }
    if (this.authService.checkSalespointProperty('fiscal_procedures_by')) {
      this.fiscalService.getShiftStatus();
    }
    this.subscriptions.push(this.utilService.fixedSnack.subscribe(value => {
      this.changeDetector.detectChanges();
    }));
    this.subscriptions.push(this.viewService.changeDetectionEmitter.subscribe(() => {
      this.changeDetector.detectChanges();
    }));
    this.checkIfNotificationsNeeded();
    this.subscriptions.push(this.storageService.changes.subscribe(({ key, value }) => {
      if (this.caller) {
        this.caller = false;
        return;
      }
      if (key === AuthService.STORAGE_AUTH_ACCESS_TOKEN) {
        if (!this.routeService.isWindow && this.routeService.checkRoute(['sales-terminal', 'cart'])) {
          this.storageService.remove(EventPurchaseComponent.STORAGE_EVENT_PURCHASE_ID);
          this.storageService.remove(EventPurchaseComponent.STORAGE_EVENT_IS_BO);
          this.storageService.remove(RouteService.STORAGE_ROUTE_IS_PURCHASE_VIEW);
          this.storageService.set(CartService.STORAGE_CART_CHECKOUT, moment.now());
        }
        if (this.routeService.isWindow && !this.storageService.get(AuthService.STORAGE_AUTH_SKIP_CUSTOMER_SCREEN_CLOSURE)) {
          this.closeWindow();
        }
      }
      if (key === AuthService.STORAGE_AUTH_USER_LOGGED_OUT && value) {
        if (this.routeService.isWindow) {
          this.closeWindow();
        } else {
          if (this.authService.caller) {
            this.authService.caller = false;
          } else {
            this.authService.reloadPage();
          }
        }
      }
      if (key === AuthService.STORAGE_AUTH_USER_LOGGED_IN && value) {
        this.authService.reloadPage();
      }
      if (key === AuthService.STORAGE_AUTH_RELOAD_PAGE && value) {
        this.authService.reloadPage();
      }
      if (key === UtilService.STORAGE_CONTENT_SCROLL && value >= 0) {
        setTimeout(() => {
          this.pageContent.nativeElement.scrollTop = value;
        }, 0);
      }
    }));
    this.storageService.changes.subscribe(({ key }) => {
      if (key === LanguageService.STORAGE_LANGUAGE_SELECTED) {
        if (!this.languageService.caller) {
          this.languageService.checkLang();
        } else {
          this.languageService.caller = false;
        }
      }
    });
    this.subscriptions.push(
      this.scrollSubject.pipe(
        // Throttling scroll helps keep sticky forms from jumping on slow scrolling.
        throttleTime(100, asyncScheduler, { leading: true, trailing: true }),
      ).subscribe(e => {
        const nextScrolled = e.target.scrollTop > 0;
        if (this.prevScrolled !== nextScrolled) {
          this.isScrolled = nextScrolled;
          this.prevScrolled = nextScrolled;
          this.changeDetector.detectChanges();
        }
        this.utilService.contentScrolledTop = e.target.scrollTop;
        this.utilService.contentScrolled.emit(this.isScrolled);
      })
    );
  }

  ngAfterViewInit(): void {
    this.updateSidenav();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.subscriptions = [];
    this.utilService.hasHeader = false;
  }

  ngAfterContentChecked(): void {
    this.updateSidenav();
    this.changeDetector.detectChanges();
  }

  updateSidenav(): void {
    this.utilService.sidenavWidth = this.sidenav._getWidth();
  }

  toggleMenu(): void {
    this.sidenavExpanded = !this.sidenavExpanded;
    this.storageService.set(LayoutComponent.STORAGE_LAYOUT_MENU_EXTENDED, this.sidenavExpanded);
  }

  openCategory(category: MenuCategory): void {
    this.openFirstCategoryItem(category);
  }

  isActive(route: string): boolean {
    const menuPath: string = '/app/' + this.currentRoute + route;
    if (this.router.url.split('/').length > menuPath.split('/').length) {
      if (!route.length) {
        let isActive: boolean = true;
        this.sidenavActiveRoute.items.concat(this.sidenavActiveRoute?.primaryItems || []).forEach(item => {
          if (item.route.length) {
            if (this.router.url.includes(menuPath + item.route)) {
              isActive = false;
            }
          }
        });
        return isActive;
      } else {
        return this.router.url.includes(menuPath);
      }
    }
    return this.router.url.split('?')[0] === menuPath;
  }

  openFirstCategoryItem(category: MenuCategory): void {
    const firstItem: MenuItem = find(category.items, item => item.canAccess());
    if (firstItem) {
      this.routeService.navigate('/app/' + this.currentRoute + firstItem.route);
    }
  }

  getActiveCategory(): void {
    this.activeCategory = null;
    this.sidenavExtended = false;
    const currentSidenavRoute: MenuItem = this.sidenavActiveRoute;
    (currentSidenavRoute.categories.concat(currentSidenavRoute?.primaryCategories || [])).forEach(category => {
      const route = '/' + this.currentUrl.split('/')[3];
      if (category.canAccess() && find(category.items, { route: route.split('?')[0] })) {
        this.activeCategory = category;
      }
    });
    if (this.activeCategory) {
      this.sidenavExtended = true;
    }
  }

  isActiveCategory(categoryLabel: string): boolean {
    return this.activeCategory?.label === categoryLabel;
  }

  public updatePrevUrl(): void {
    this.routeService.updatePrevUrl();
  }

  public goToHomepage(): void {
    this.authService.navigateToBaseUrl();
  }

  get currentRoute(): string {
    return this.currentUrl?.split('/')[2];
  }

  get sidenavActiveRoute(): MenuItem {
    return find(this.menuItems, { route: this.currentRoute }) || { route: '', label: '', categories: [], items: [] };
  }

  onActivate(e: any, outlet: HTMLElement): void {
    outlet.scrollTop = 0;
  }

  onScroll(e: any): void {
    this.scrollSubject.next(e);
  }

  closeWindow(): void {
    window.close();
  }

  openNav(): void {
    const dialogRef: MatDialogRef<NavMenuComponent> = this.dialog.open(NavMenuComponent, {
      width: '50%',
      data: {
        menuItems: this.menuItems,
        currentUrl: this.currentUrl
      },
      panelClass: ['nav-modal', 'without-footer']
    });
    dialogRef.afterOpened().subscribe(_ => {
      this.navOpen = true;
    });
    dialogRef.afterClosed().subscribe(route => {
      this.navOpen = false;
      if (route) {
        this.routeService.replaceUrl('/app/' + route);
      }
    });
  }

  checkIfNotificationsNeeded(): void {
    const check: string = this.storageService.get(AuthService.STORAGE_AUTH_LEGAL_INVITATION_ACCEPTED);
    if (check?.length) {
      this.openLegalAddedModal(check);
    }
  }

  openLegalAddedModal(check: string): void {
    const legals: string[] = JSON.parse(check);
    const welcomeDialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: this.translateService.instant('general.api_legal_representative_welcome_title'),
        description: this.translateService.instant('general.api_legal_representative_welcome_description', { legals: '<b>' + legals.join(', ') + '</b>' }),
        buttonOk: this.translateService.instant('general.api_legal_representative_welcome_ok_button'),
        buttonCancelHidden: true
      }
    });
    welcomeDialogRef.afterClosed().subscribe((res: any) => {
      this.storageService.remove(AuthService.STORAGE_AUTH_LEGAL_INVITATION_ACCEPTED);
      this.authService.showBankVerificationNotification();
    });
  }
}
